import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Typography,
  Alert,
  CircularProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Chip,
  IconButton,
  Tooltip,
  Collapse,
  LinearProgress
} from '@mui/material';
import {
  CheckCircle as CheckIcon,
  Error as ErrorIcon,
  Warning as WarningIcon,
  KeyboardArrowDown as ExpandIcon,
  KeyboardArrowUp as CollapseIcon,
  AutoFixHigh as AutoMapIcon
} from '@mui/icons-material';

// System field definitions with auto-mapping aliases
const SYSTEM_FIELDS = {
  sku: {
    label: 'SKU',
    required: true,
    aliases: ['sku', 'product_sku', 'item_sku', 'product_code', 'item_code', 'code', 'sku_code', 'barcode', 'product_id', 'item_id'],
    description: 'Product identifier'
  },
  counted_quantity: {
    label: 'Counted Quantity',
    required: true,
    aliases: ['counted_quantity', 'quantity', 'qty', 'count', 'counted', 'counted_qty', 'physical_qty', 'actual_qty', 'stock_count', 'counted_count', 'physical_count'],
    description: 'Physical count quantity',
    validate: (val) => {
      const num = parseFloat(val);
      return !isNaN(num) && num >= 0;
    }
  },
  warehouse_code: {
    label: 'Warehouse Code',
    required: true,
    aliases: ['warehouse_code', 'warehouse', 'wh_code', 'location', 'location_code', 'wh', 'store', 'store_code', 'facility', 'facility_code'],
    description: 'Warehouse or location identifier'
  },
  count_date: {
    label: 'Count Date',
    required: true,
    aliases: ['count_date', 'date', 'counted_date', 'physical_date', 'stock_date', 'inventory_date', 'counted_on'],
    description: 'Date of physical count (YYYY-MM-DD)',
    validate: (val) => {
      // Accept various date formats
      const date = new Date(val);
      return !isNaN(date.getTime());
    }
  },
  batch_number: {
    label: 'Batch Number',
    required: false,
    aliases: ['batch_number', 'batch', 'lot_number', 'lot', 'batch_id', 'lot_id', 'batch_code'],
    description: 'Batch or lot number (optional)'
  },
  gtin: {
    label: 'GTIN',
    required: false,
    aliases: ['gtin', 'ean', 'upc', 'gtin13', 'gtin14', 'ean13'],
    description: 'Global Trade Item Number (optional)'
  },
  asin: {
    label: 'ASIN',
    required: false,
    aliases: ['asin', 'amazon_sku', 'amazon_asin'],
    description: 'Amazon Standard Identification Number (optional)'
  },
  expiry_date: {
    label: 'Expiry Date',
    required: false,
    aliases: ['expiry_date', 'expiry', 'expiration_date', 'expiration', 'exp_date', 'best_before', 'use_by'],
    description: 'Product expiry date (optional)'
  },
  unit_cost: {
    label: 'Unit Cost',
    required: false,
    aliases: ['unit_cost', 'cost', 'price', 'unit_price', 'cost_price', 'purchase_price'],
    description: 'Cost per unit (optional)',
    validate: (val) => {
      if (!val || val === '') return true; // Optional
      const num = parseFloat(val);
      return !isNaN(num) && num >= 0;
    }
  }
};

/**
 * Auto-map CSV columns to system fields based on aliases
 */
function autoMapColumns(csvHeaders) {
  const mapping = {};
  const normalizedHeaders = csvHeaders.map(h => h.toLowerCase().trim().replace(/[^a-z0-9]/g, '_'));

  Object.entries(SYSTEM_FIELDS).forEach(([fieldKey, fieldDef]) => {
    // Find best matching header
    for (const alias of fieldDef.aliases) {
      const normalizedAlias = alias.toLowerCase().replace(/[^a-z0-9]/g, '_');
      const headerIndex = normalizedHeaders.findIndex(h => h === normalizedAlias || h.includes(normalizedAlias) || normalizedAlias.includes(h));

      if (headerIndex !== -1 && !Object.values(mapping).includes(csvHeaders[headerIndex])) {
        mapping[fieldKey] = csvHeaders[headerIndex];
        break;
      }
    }
  });

  return mapping;
}

/**
 * Validate a single row against the mapping
 */
function validateRow(row, mapping) {
  const errors = [];
  const warnings = [];

  Object.entries(SYSTEM_FIELDS).forEach(([fieldKey, fieldDef]) => {
    const csvColumn = mapping[fieldKey];
    const value = csvColumn ? row[csvColumn] : undefined;

    if (fieldDef.required) {
      if (!value || value.toString().trim() === '') {
        errors.push(`Missing required field: ${fieldDef.label}`);
      } else if (fieldDef.validate && !fieldDef.validate(value)) {
        errors.push(`Invalid ${fieldDef.label}: "${value}"`);
      }
    } else if (value && fieldDef.validate && !fieldDef.validate(value)) {
      warnings.push(`Invalid ${fieldDef.label}: "${value}"`);
    }
  });

  return { errors, warnings, isValid: errors.length === 0 };
}

/**
 * ImportMappingStep - Step 2: Map CSV columns to system fields
 * Shows column mapping dropdowns and data preview with validation
 */
function ImportMappingStep({
  parsedData,
  mapping,
  onMappingChange,
  loading,
  error
}) {
  const [expandedRows, setExpandedRows] = useState(new Set());
  const [showAllRows, setShowAllRows] = useState(false);

  const csvHeaders = parsedData?.headers || [];
  const csvRows = parsedData?.rows || [];

  // Auto-map on initial load if no mapping exists
  useEffect(() => {
    if (csvHeaders.length > 0 && Object.keys(mapping).length === 0) {
      const autoMapping = autoMapColumns(csvHeaders);
      onMappingChange(autoMapping);
    }
  }, [csvHeaders, mapping, onMappingChange]);

  // Validate all rows
  const validationResults = useMemo(() => {
    return csvRows.map((row, index) => ({
      rowIndex: index,
      ...validateRow(row, mapping)
    }));
  }, [csvRows, mapping]);

  // Calculate summary statistics
  const stats = useMemo(() => {
    const total = validationResults.length;
    const valid = validationResults.filter(r => r.isValid).length;
    const withErrors = validationResults.filter(r => r.errors.length > 0).length;
    const withWarnings = validationResults.filter(r => r.warnings.length > 0 && r.isValid).length;

    // Check required fields are mapped
    const requiredFields = Object.entries(SYSTEM_FIELDS)
      .filter(([_, def]) => def.required)
      .map(([key, _]) => key);
    const mappedRequired = requiredFields.filter(f => mapping[f]);
    const missingRequired = requiredFields.filter(f => !mapping[f]);

    return {
      total,
      valid,
      withErrors,
      withWarnings,
      requiredFields,
      mappedRequired,
      missingRequired,
      allRequiredMapped: missingRequired.length === 0
    };
  }, [validationResults, mapping]);

  const handleMappingChange = (systemField, csvColumn) => {
    const newMapping = { ...mapping };
    if (csvColumn === '') {
      delete newMapping[systemField];
    } else {
      newMapping[systemField] = csvColumn;
    }
    onMappingChange(newMapping);
  };

  const handleAutoMap = () => {
    const autoMapping = autoMapColumns(csvHeaders);
    onMappingChange(autoMapping);
  };

  const toggleRowExpand = (index) => {
    const newExpanded = new Set(expandedRows);
    if (newExpanded.has(index)) {
      newExpanded.delete(index);
    } else {
      newExpanded.add(index);
    }
    setExpandedRows(newExpanded);
  };

  // Preview rows (first 10 or all)
  const previewRows = showAllRows ? csvRows : csvRows.slice(0, 10);

  if (loading) {
    return (
      <Box sx={{ textAlign: 'center', py: 4 }}>
        <CircularProgress size={48} sx={{ mb: 2 }} />
        <Typography>Parsing CSV file...</Typography>
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        Step 2: Map Columns & Preview
      </Typography>
      <Typography variant="body2" color="textSecondary" sx={{ mb: 3 }}>
        Map your CSV columns to system fields. We've auto-detected some mappings for you.
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {/* Mapping Summary */}
      <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
          <Typography variant="subtitle2">Column Mapping</Typography>
          <Tooltip title="Auto-detect column mappings">
            <IconButton size="small" onClick={handleAutoMap}>
              <AutoMapIcon />
            </IconButton>
          </Tooltip>
        </Box>

        {/* Required fields status */}
        {!stats.allRequiredMapped && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            Missing required mappings: {stats.missingRequired.map(f => SYSTEM_FIELDS[f].label).join(', ')}
          </Alert>
        )}

        {/* Mapping dropdowns - 2 columns */}
        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 2 }}>
          {Object.entries(SYSTEM_FIELDS).map(([fieldKey, fieldDef]) => (
            <FormControl key={fieldKey} size="small" fullWidth>
              <InputLabel>
                {fieldDef.label}
                {fieldDef.required && <span style={{ color: 'red' }}> *</span>}
              </InputLabel>
              <Select
                value={mapping[fieldKey] || ''}
                onChange={(e) => handleMappingChange(fieldKey, e.target.value)}
                label={`${fieldDef.label}${fieldDef.required ? ' *' : ''}`}
                error={fieldDef.required && !mapping[fieldKey]}
              >
                <MenuItem value="">
                  <em>-- Not Mapped --</em>
                </MenuItem>
                {csvHeaders.map((header) => (
                  <MenuItem
                    key={header}
                    value={header}
                    disabled={Object.values(mapping).includes(header) && mapping[fieldKey] !== header}
                  >
                    {header}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          ))}
        </Box>
      </Paper>

      {/* Validation Summary */}
      <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
        <Typography variant="subtitle2" gutterBottom>
          Validation Summary
        </Typography>

        <Box sx={{ mb: 2 }}>
          <LinearProgress
            variant="determinate"
            value={stats.total > 0 ? (stats.valid / stats.total) * 100 : 0}
            color={stats.valid === stats.total ? 'success' : 'warning'}
            sx={{ height: 8, borderRadius: 1 }}
          />
        </Box>

        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
          <Chip
            icon={<CheckIcon />}
            label={`${stats.valid} Valid`}
            color="success"
            size="small"
            variant="outlined"
          />
          {stats.withErrors > 0 && (
            <Chip
              icon={<ErrorIcon />}
              label={`${stats.withErrors} Errors`}
              color="error"
              size="small"
              variant="outlined"
            />
          )}
          {stats.withWarnings > 0 && (
            <Chip
              icon={<WarningIcon />}
              label={`${stats.withWarnings} Warnings`}
              color="warning"
              size="small"
              variant="outlined"
            />
          )}
          <Chip
            label={`${stats.total} Total Rows`}
            size="small"
            variant="outlined"
          />
        </Box>
      </Paper>

      {/* Data Preview Table */}
      <Paper variant="outlined">
        <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
          <Typography variant="subtitle2">
            Data Preview ({showAllRows ? csvRows.length : Math.min(10, csvRows.length)} of {csvRows.length} rows)
          </Typography>
        </Box>

        <TableContainer sx={{ maxHeight: 400 }}>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell padding="checkbox" sx={{ width: 40 }}></TableCell>
                <TableCell sx={{ width: 60 }}>Status</TableCell>
                <TableCell sx={{ width: 50 }}>Row</TableCell>
                {Object.entries(SYSTEM_FIELDS).map(([key, def]) => (
                  mapping[key] && (
                    <TableCell key={key}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        {def.label}
                        {def.required && <span style={{ color: 'red' }}>*</span>}
                      </Box>
                    </TableCell>
                  )
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {previewRows.map((row, index) => {
                const validation = validationResults[index];
                const hasIssues = validation.errors.length > 0 || validation.warnings.length > 0;

                return (
                  <React.Fragment key={index}>
                    <TableRow
                      hover
                      sx={{
                        backgroundColor: validation.errors.length > 0
                          ? 'error.lighter'
                          : validation.warnings.length > 0
                            ? 'warning.lighter'
                            : 'inherit'
                      }}
                    >
                      <TableCell padding="checkbox">
                        {hasIssues && (
                          <IconButton size="small" onClick={() => toggleRowExpand(index)}>
                            {expandedRows.has(index) ? <CollapseIcon /> : <ExpandIcon />}
                          </IconButton>
                        )}
                      </TableCell>
                      <TableCell>
                        {validation.isValid ? (
                          validation.warnings.length > 0 ? (
                            <Tooltip title={`${validation.warnings.length} warning(s)`}>
                              <WarningIcon color="warning" fontSize="small" />
                            </Tooltip>
                          ) : (
                            <CheckIcon color="success" fontSize="small" />
                          )
                        ) : (
                          <Tooltip title={`${validation.errors.length} error(s)`}>
                            <ErrorIcon color="error" fontSize="small" />
                          </Tooltip>
                        )}
                      </TableCell>
                      <TableCell>{index + 1}</TableCell>
                      {Object.entries(SYSTEM_FIELDS).map(([key, def]) => (
                        mapping[key] && (
                          <TableCell key={key}>
                            <Typography
                              variant="body2"
                              noWrap
                              sx={{
                                maxWidth: 150,
                                color: def.required && !row[mapping[key]] ? 'error.main' : 'inherit'
                              }}
                            >
                              {row[mapping[key]] || '-'}
                            </Typography>
                          </TableCell>
                        )
                      ))}
                    </TableRow>

                    {/* Expanded error/warning details */}
                    {hasIssues && (
                      <TableRow>
                        <TableCell colSpan={3 + Object.keys(mapping).length} sx={{ py: 0 }}>
                          <Collapse in={expandedRows.has(index)}>
                            <Box sx={{ p: 2, backgroundColor: 'grey.50' }}>
                              {validation.errors.map((err, i) => (
                                <Typography key={i} variant="body2" color="error" sx={{ mb: 0.5 }}>
                                  • {err}
                                </Typography>
                              ))}
                              {validation.warnings.map((warn, i) => (
                                <Typography key={i} variant="body2" color="warning.dark" sx={{ mb: 0.5 }}>
                                  • {warn}
                                </Typography>
                              ))}
                            </Box>
                          </Collapse>
                        </TableCell>
                      </TableRow>
                    )}
                  </React.Fragment>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>

        {csvRows.length > 10 && (
          <Box sx={{ p: 2, textAlign: 'center', borderTop: 1, borderColor: 'divider' }}>
            <Typography
              variant="body2"
              color="primary"
              sx={{ cursor: 'pointer', textDecoration: 'underline' }}
              onClick={() => setShowAllRows(!showAllRows)}
            >
              {showAllRows ? 'Show fewer rows' : `Show all ${csvRows.length} rows`}
            </Typography>
          </Box>
        )}
      </Paper>
    </Box>
  );
}

export default ImportMappingStep;
